import { cva } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Asset } from "@/components/ui/Asset";
import { Container } from "@/components/ui/Container";
import { Reveal } from "@/components/ui/Reveal";
import { Typography } from "@/components/ui/Typography";
import type { AssetStoryblok } from "@/generated/sb/sb-types";
import type { Media } from "@/lib/utils/media";
import { HeroCountdownLive } from "./HeroCountdownLive";
import { HeroCounter } from "./HeroCounter";

export type HeroTextPosition = "center" | "bottomLeft" | "centerLeft";

export interface HeroCountdown {
	days: number | string;
	hours: number | string;
	mins: number | string;
}

export interface HeroProps extends ComponentProps<"section"> {
	assets: (AssetStoryblok | undefined)[];
	caption?: string;
	title?: string;
	description?: string;
	countdown?: HeroCountdown;
	countdownTo?: string;
	actions?: ReactNode;
	textPosition?: HeroTextPosition;
	fullWidth?: boolean;
	priority?: boolean;
	isHeader?: boolean;
}

const rootStyles =
	"relative w-full overflow-clip isolate text-text-sand-text-2 h-[calc(80vh_-_48px)] md:h-[calc(80vh_-_96px)] max-h-[900px] flex items-end";

const contentLayoutStyles = cva("relative flex w-full", {
	variants: {
		textPosition: {
			center: "flex-col items-center justify-center",
			bottomLeft: "flex-col items-stretch md:flex-row md:items-end",
			centerLeft: "flex-col items-stretch md:flex-row md:items-center",
		},
	},
	defaultVariants: { textPosition: "center" },
});

const primaryColumnStyles = cva("flex flex-col gap-8 min-w-0", {
	variants: {
		textPosition: {
			center: "mx-auto w-full max-w-[730px] items-center text-center py-grid-section-vertical-2xl",
			bottomLeft: "w-full md:flex-1 items-start pb-8",
			centerLeft: "w-full md:flex-1 items-start py-80",
		},
	},
	defaultVariants: { textPosition: "center" },
});

const textBlockStyles = cva("flex flex-col gap-grid-component-spacing-md w-full", {
	variants: {
		textPosition: {
			center: "items-center text-center",
			bottomLeft: "items-start text-left",
			centerLeft: "items-start text-left",
		},
	},
	defaultVariants: { textPosition: "center" },
});

const actionsStyles = cva("flex gap-grid-grid-gutter-xl w-full", {
	variants: {
		textPosition: {
			center: "items-center justify-center",
			bottomLeft: "items-end justify-start",
			centerLeft: "items-end justify-start",
		},
	},
	defaultVariants: { textPosition: "center" },
});

const secondaryColumnStyles = cva("flex flex-col min-w-0 w-full", {
	variants: {
		textPosition: {
			center: "hidden",
			// bottomLeft hides the side column on mobile — the countdown
			// renders inline in the primary column instead (see contentLayer).
			bottomLeft: "hidden md:flex md:flex-1 md:items-end justify-end pb-8",
			centerLeft: "items-start md:flex-1 justify-center py-80",
		},
	},
	defaultVariants: { textPosition: "center" },
});

const renderCountdown = (countdownTo?: string, countdown?: HeroCountdown) =>
	countdownTo ? (
		<HeroCountdownLive countdownTo={countdownTo} size="small" />
	) : countdown ? (
		<HeroCounter {...countdown} size="small" />
	) : null;

export const Hero = ({
	assets,
	caption,
	title,
	description,
	countdown,
	countdownTo,
	actions,
	textPosition = "center",
	fullWidth = false,
	priority = false,
	className,
	isHeader,
}: HeroProps) => {
	const visibleAssets = assets.slice(0, 2);
	const hasTwoAssets = visibleAssets.filter(Boolean).length === 2;
	const hasText = Boolean(caption || title || description);
	const descriptionInSide = textPosition === "centerLeft" && Boolean(description);
	const hasCountdown = Boolean(countdownTo ?? countdown);
	const counterInSide = textPosition === "bottomLeft" && hasCountdown;
	const renderSide = descriptionInSide || counterInSide;
	const imageLayer = (
		<div aria-hidden className="absolute inset-0 flex" data-part="image-wrapper">
			{visibleAssets.map((asset, index) => {
				if (!asset?.filename) return null;

				return (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: order is stable and bounded to two
						key={index}
						className="relative flex-1 min-w-0 overflow-clip"
					>
						<Asset
							asset={asset}
							priority={priority}
							sizes={hasTwoAssets ? "100vw" : "200vw"}
							className="object-cover"
							// placeholder={asset.blurDataURL ? "blur" : undefined}
							// blurDataURL={asset.blurDataURL}
						/>
						<div className="absolute inset-0 bg-overlay-3" />
					</div>
				);
			})}
		</div>
	);

	const contentLayer = (
		<div className={contentLayoutStyles({ textPosition })}>
			<div className={primaryColumnStyles({ textPosition })}>
				{hasText && (
					<div className={textBlockStyles({ textPosition })}>
						{caption && (
							<Reveal animation="fade-bottom" delay={0}>
								<Typography variant="label-md-reg" className="leading-[1.15] w-full">
									{caption}
								</Typography>
							</Reveal>
						)}
						{title && (
							<Reveal animation="fade-bottom" delay={10}>
								<Typography
									as={isHeader ? "h1" : "h2"}
									variant="heading-display-md"
									className="w-full"
								>
									{title}
								</Typography>
							</Reveal>
						)}
						{description && !descriptionInSide && (
							<Reveal animation="fade-bottom" delay={200}>
								<Typography variant="body-md-reg" className="leading-normal w-full">
									{description}
								</Typography>
							</Reveal>
						)}
					</div>
				)}

				{hasCountdown && !counterInSide && renderCountdown(countdownTo, countdown)}

				{actions && (
					<Reveal animation="fade-bottom" delay={250}>
						<div className={actionsStyles({ textPosition })}>{actions}</div>
					</Reveal>
				)}

				{hasCountdown && counterInSide && (
					<Reveal animation="fade-bottom" delay={250}>
						<div className="md:hidden">{renderCountdown(countdownTo, countdown)}</div>
					</Reveal>
				)}
			</div>

			{renderSide && (
				<Reveal animation="fade-bottom" delay={300}>
					<div className={secondaryColumnStyles({ textPosition })}>
						{counterInSide && hasCountdown && renderCountdown(countdownTo, countdown)}
						{descriptionInSide && (
							<Typography variant="body-md-reg" className="leading-normal w-full">
								{description}
							</Typography>
						)}
					</div>
				</Reveal>
			)}
		</div>
	);

	if (fullWidth) {
		return (
			<section className={twMerge(rootStyles, className)}>
				{imageLayer}
				<Container as="div" className="relative">
					{contentLayer}
				</Container>
			</section>
		);
	}

	return (
		<Container as="section">
			<div className={twMerge(rootStyles, className)}>
				{imageLayer}
				<Container as="div" size="large">
					{contentLayer}
				</Container>
			</div>
		</Container>
	);
};
